import { Backspace } from '@phosphor-icons/react';
import { haptic } from '../lib/telegram';
import { useLang } from '../lib/i18n';
import { cleanAmountInput } from '../lib/format';

/** One-thumb numeric keypad; avoids the system keyboard covering the amount. */
export function Keypad({ onChange, decimals }: { onChange: React.Dispatch<React.SetStateAction<string>>; decimals: number }) {
  const lang = useLang();
  const sep = lang === 'ru' ? ',' : '.';
  const press = (k: string) => {
    haptic.tap();
    if (k === 'del') return onChange((v) => v.slice(0, -1));
    if (k === '.') return onChange((v) => (decimals === 0 || v.includes('.') ? v : v ? `${v}.` : '0.'));
    onChange((v) => cleanAmountInput(v + k, decimals));
  };
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'del'];
  return (
    <div className="keypad" role="group" aria-label="Keypad">
      {keys.map((k) => (
        <button
          key={k}
          type="button"
          className="keypad__key num"
          aria-label={k === 'del' ? 'Delete' : k === '.' ? 'Decimal separator' : k}
          onClick={() => press(k)}
          onContextMenu={(e) => {
            if (k === 'del') {
              e.preventDefault();
              onChange('');
            }
          }}
        >
          {k === 'del' ? <Backspace size={24} /> : k === '.' ? sep : k}
        </button>
      ))}
    </div>
  );
}

/** The amount being typed, set large; separator follows the language. */
export function AmountDisplay({ value, unit, invalid, sub }: { value: string; unit: string; invalid?: boolean; sub?: React.ReactNode }) {
  const lang = useLang();
  const shown = value ? value.replace('.', lang === 'ru' ? ',' : '.') : '0';
  const [int = '0', frac] = shown.split(/[.,]/);
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const len = grouped.length + (frac?.length ?? 0);
  const size = len > 12 ? 'sm' : len > 8 ? 'md' : 'lg';
  return (
    <div className={`amount-display amount-display--${size}${invalid ? ' is-invalid' : ''}${value ? '' : ' is-empty'}`} aria-live="polite">
      <span className="amount-display__value num">
        {grouped}
        {frac !== undefined && (
          <span className="amount-display__frac">
            {lang === 'ru' ? ',' : '.'}
            {frac}
          </span>
        )}
        <span className="amount-display__caret" aria-hidden="true" />
      </span>
      <span className="amount-display__unit">{unit}</span>
      {sub && <div className="amount-display__sub">{sub}</div>}
    </div>
  );
}
